import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Shield, Plus, Search, Download, Trash2, FileText, RefreshCw, Loader2,
  AlertTriangle, CheckCircle, Clock, User, Lock, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { formatBackendDate } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const REQUEST_TYPES = [
  ['access', 'Access request'],
  ['correction', 'Correction'],
  ['restriction', 'Restriction of processing'],
  ['deletion', 'Deletion (erasure) request'],
  ['objection', 'Objection to processing'],
  ['other', 'Other'],
];

const STATUSES = [
  ['new', 'New'],
  ['in_progress', 'In progress'],
  ['awaiting_info', 'Awaiting information'],
  ['on_hold', 'On hold'],
  ['completed', 'Completed'],
  ['closed', 'Closed'],
  ['rejected', 'Rejected'],
];

const STATUS_COLOR = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  awaiting_info: 'bg-purple-100 text-purple-700',
  on_hold: 'bg-slate-100 text-slate-700',
  completed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const label = (pairs, key) => (pairs.find((p) => p[0] === key)?.[1]) || key;

const emptyForm = () => ({
  request_type: 'access',
  subject_name: '',
  subject_email: '',
  subject_type: 'employee',
  linked_employee_id: '',
  linked_service_user_id: '',
  received_date: '',
  due_date: '',
  assigned_owner: '',
  notes: '',
});

export default function PrivacyRequestsPage() {
  const { token, isAdmin } = useAuth();
  const authHdr = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // subject lookup
  const [lookupTerm, setLookupTerm] = useState('');
  const [lookupResults, setLookupResults] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  // detail
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [recordsSummary, setRecordsSummary] = useState(null);
  const [deletionReview, setDeletionReview] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/privacy/requests${params}`, authHdr);
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load privacy requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { if (token) load(); }, [load, token]);

  const runLookup = async () => {
    if ((lookupTerm || '').trim().length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    setLookupBusy(true);
    try {
      const res = await axios.get(`${API}/privacy/subject-lookup?q=${encodeURIComponent(lookupTerm.trim())}`, authHdr);
      setLookupResults(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupBusy(false);
    }
  };

  const linkSubject = (candidate) => {
    setForm((f) => ({
      ...f,
      subject_name: candidate.name || f.subject_name,
      subject_email: candidate.email || f.subject_email,
      subject_type: candidate.type,
      linked_employee_id: candidate.type === 'employee' ? candidate.id : '',
      linked_service_user_id: candidate.type === 'service_user' ? candidate.id : '',
    }));
    toast.success(`Linked ${candidate.name}`);
  };

  const createRequest = async () => {
    if (!form.subject_name.trim()) { toast.error('Data subject name is required'); return; }
    setSaving(true);
    try {
      const body = {
        request_type: form.request_type,
        subject: {
          name: form.subject_name.trim(),
          email: form.subject_email.trim() || null,
          type: form.subject_type,
          linked_employee_id: form.linked_employee_id || null,
          linked_service_user_id: form.linked_service_user_id || null,
        },
        received_date: form.received_date || null,
        due_date: form.due_date || null,
        assigned_owner: form.assigned_owner || null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/privacy/requests`, body, authHdr);
      toast.success('Privacy request logged');
      setCreateOpen(false);
      setForm(emptyForm());
      setLookupResults(null);
      setLookupTerm('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create request');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (req) => {
    setDetail(req);
    setDetailStatus(req.status);
    setRecordsSummary(null);
    setDeletionReview(null);
    setActionNote('');
    try {
      const res = await axios.get(`${API}/privacy/requests/${req.id}`, authHdr);
      setDetail(res.data);
      setDetailStatus(res.data.status);
    } catch (e) { /* keep list copy */ }
  };

  const saveStatus = async () => {
    setDetailBusy(true);
    try {
      const res = await axios.patch(`${API}/privacy/requests/${detail.id}`, { status: detailStatus }, authHdr);
      setDetail(res.data);
      toast.success('Status updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setDetailBusy(false); }
  };

  const addAction = async () => {
    if (!actionNote.trim()) { toast.error('Enter an action / evidence note'); return; }
    setDetailBusy(true);
    try {
      const res = await axios.post(`${API}/privacy/requests/${detail.id}/actions`,
        { note: actionNote.trim(), action_type: 'action_taken' }, authHdr);
      setDetail(res.data);
      setActionNote('');
      toast.success('Action recorded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record action');
    } finally { setDetailBusy(false); }
  };

  const searchRecords = async () => {
    setDetailBusy(true);
    setRecordsSummary(null);
    try {
      const res = await axios.get(`${API}/privacy/requests/${detail.id}/records`, authHdr);
      setRecordsSummary(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Record search failed');
    } finally { setDetailBusy(false); }
  };

  const exportZip = async () => {
    setDetailBusy(true);
    try {
      const res = await axios.get(`${API}/privacy/requests/${detail.id}/export`,
        { ...authHdr, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Privacy_Export_${detail.reference}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed — is the request linked to a subject?');
    } finally { setDetailBusy(false); }
  };

  const runDeletionReview = async () => {
    setDetailBusy(true);
    setDeletionReview(null);
    try {
      const res = await axios.get(`${API}/privacy/requests/${detail.id}/deletion-review`, authHdr);
      setDeletionReview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deletion review failed');
    } finally { setDetailBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Lock className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <h2 className="text-lg font-semibold text-slate-800">Restricted</h2>
            <p className="text-slate-500 mt-1">The privacy / DSAR workflow is available to administrators only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-teal-600" /> Privacy &amp; DSAR Requests
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Log and evidence data subject access, correction, restriction, deletion and objection requests.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="w-4 h-4 mr-1" /> New request</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Requests</CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : requests.length === 0 ? (
            <div className="py-10 text-center text-slate-400">No privacy requests logged yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Data subject</th>
                    <th className="py-2 pr-3">Received</th>
                    <th className="py-2 pr-3">Due</th>
                    <th className="py-2 pr-3">Owner</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-medium text-slate-800">{r.reference}</td>
                      <td className="py-2 pr-3">{label(REQUEST_TYPES, r.request_type)}</td>
                      <td className="py-2 pr-3">{r.data_subject?.name}</td>
                      <td className="py-2 pr-3">{r.received_date ? formatBackendDate(r.received_date) : '—'}</td>
                      <td className="py-2 pr-3">{r.due_date ? formatBackendDate(r.due_date) : '—'}</td>
                      <td className="py-2 pr-3">{r.assigned_owner || '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge className={STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700'}>{label(STATUSES, r.status)}</Badge>
                      </td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(r)}>Open</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log a privacy / DSAR request</DialogTitle>
            <DialogDescription>Record the request and identify the data subject.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Request type</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border p-3 bg-slate-50 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><User className="w-4 h-4" /> Identify the data subject</div>
              <div className="flex gap-2">
                <Input placeholder="Search staff or service users…" value={lookupTerm}
                  onChange={(e) => setLookupTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runLookup()} />
                <Button variant="outline" onClick={runLookup} disabled={lookupBusy}>
                  {lookupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {lookupResults && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {[...(lookupResults.employees || []), ...(lookupResults.service_users || [])].length === 0 && (
                    <div className="text-xs text-slate-400">No matches.</div>
                  )}
                  {[...(lookupResults.employees || []), ...(lookupResults.service_users || [])].map((c) => (
                    <button key={`${c.type}-${c.id}`} type="button"
                      onClick={() => linkSubject(c)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-sm flex items-center justify-between">
                      <span>{c.name} {c.email ? <span className="text-slate-400">· {c.email}</span> : null}</span>
                      <Badge variant="outline">{c.type === 'employee' ? 'Staff' : 'Service user'}</Badge>
                    </button>
                  ))}
                </div>
              )}
              {(form.linked_employee_id || form.linked_service_user_id) && (
                <div className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Linked to {form.subject_type === 'employee' ? 'staff' : 'service user'} record
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Subject name</Label>
                <Input value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} />
              </div>
              <div>
                <Label>Subject email</Label>
                <Input value={form.subject_email} onChange={(e) => setForm({ ...form, subject_email: e.target.value })} />
              </div>
              <div>
                <Label>Subject type</Label>
                <Select value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Staff member</SelectItem>
                    <SelectItem value="service_user">Service user</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assigned owner</Label>
                <Input value={form.assigned_owner} onChange={(e) => setForm({ ...form, assigned_owner: e.target.value })} />
              </div>
              <div>
                <Label>Received date</Label>
                <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createRequest} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Log request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.reference}
                  <Badge className={STATUS_COLOR[detail.status]}>{label(STATUSES, detail.status)}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {label(REQUEST_TYPES, detail.request_type)} · {detail.data_subject?.name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-slate-400">Received</div><div>{detail.received_date ? formatBackendDate(detail.received_date) : '—'}</div></div>
                  <div><div className="text-slate-400">Due</div><div>{detail.due_date ? formatBackendDate(detail.due_date) : '—'}</div></div>
                  <div><div className="text-slate-400">Owner</div><div>{detail.assigned_owner || '—'}</div></div>
                  <div><div className="text-slate-400">Completed</div><div>{detail.completion_date ? formatBackendDate(detail.completion_date) : '—'}</div></div>
                </div>

                {detail.notes && <div className="text-sm bg-slate-50 rounded p-3"><span className="text-slate-400">Notes: </span>{detail.notes}</div>}

                {/* Status update */}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label>Update status</Label>
                    <Select value={detailStatus} onValueChange={setDetailStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={saveStatus} disabled={detailBusy || detailStatus === detail.status}>Save</Button>
                </div>

                {/* Evidence / actions */}
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Evidence &amp; actions taken</div>
                  {(detail.actions || []).length === 0 ? (
                    <div className="text-xs text-slate-400">No actions recorded yet.</div>
                  ) : (
                    <ul className="space-y-2">
                      {detail.actions.map((a) => (
                        <li key={a.id} className="text-sm border-l-2 border-teal-200 pl-3">
                          <div>{a.note}</div>
                          <div className="text-xs text-slate-400">{a.actor} · {a.at ? formatBackendDate(a.at) : ''}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <Input placeholder="Record an action / evidence…" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                    <Button variant="outline" onClick={addAction} disabled={detailBusy}>Add</Button>
                  </div>
                </div>

                {/* Records + export */}
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Data subject records</div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={searchRecords} disabled={detailBusy}><Search className="w-4 h-4 mr-1" /> Search</Button>
                      <Button size="sm" onClick={exportZip} disabled={detailBusy}><Download className="w-4 h-4 mr-1" /> Export</Button>
                    </div>
                  </div>
                  {recordsSummary && (
                    <div className="text-sm">
                      <div className="text-slate-500 mb-1">{recordsSummary.total_records} records · profile {recordsSummary.profile_found ? 'found' : 'not found'}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {recordsSummary.sources.filter((s) => s.count > 0).map((s) => (
                          <div key={s.collection} className="rounded bg-slate-50 px-2 py-1.5 text-xs flex justify-between">
                            <span>{s.label}</span><span className="font-semibold">{s.count}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">{recordsSummary.coverage_note}</p>
                    </div>
                  )}
                </div>

                {/* Deletion review */}
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-700 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Deletion review (safe)</div>
                    <Button variant="outline" size="sm" onClick={runDeletionReview} disabled={detailBusy}>Run review</Button>
                  </div>
                  {deletionReview && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 text-xs bg-amber-50 text-amber-800 rounded p-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{deletionReview.disclaimer}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded bg-emerald-50 p-2"><div className="text-lg font-bold text-emerald-700">{deletionReview.counts.could_be_deleted}</div><div className="text-xs text-emerald-700">Could be deleted</div></div>
                        <div className="rounded bg-blue-50 p-2"><div className="text-lg font-bold text-blue-700">{deletionReview.counts.retained}</div><div className="text-xs text-blue-700">Retained</div></div>
                        <div className="rounded bg-amber-50 p-2"><div className="text-lg font-bold text-amber-700">{deletionReview.counts.needs_manual_legal_decision}</div><div className="text-xs text-amber-700">Manual/legal decision</div></div>
                      </div>
                      {['retained', 'needs_manual_legal_decision', 'could_be_deleted'].map((bucket) => (
                        deletionReview[bucket].length > 0 && (
                          <div key={bucket}>
                            <div className="text-xs font-semibold text-slate-600 mb-1 capitalize flex items-center gap-1">
                              {bucket === 'retained' ? <Clock className="w-3.5 h-3.5" /> : bucket === 'could_be_deleted' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                              {bucket.replace(/_/g, ' ')}
                            </div>
                            <ul className="space-y-1">
                              {deletionReview[bucket].map((x, i) => (
                                <li key={`${bucket}-${i}`} className="text-xs bg-slate-50 rounded px-2 py-1">
                                  <span className="font-medium">{x.label}</span> — {x.reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
